import './ManageBotActions.styles.scss';

import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useStompClient } from 'react-stomp-hooks';

import { getPokerRoom } from '../../constants';
import { Permission } from '../../model/enumeration/Permission.enum';
import { PokerRoom } from '../../model/enumeration/PokerRoom';
import authStore from '../../store/AuthStore';
import availableBotStore from '../../store/AvailableBotStore';
import botManagementStore from '../../store/BotManagementStore';
import notificationStore from '../../store/NotificationStore';
import permissionStore from '../../store/PermissionStore';
import tableStore from '../../store/TableStore';
import userSettingsStore from '../../store/UserSettingsStore';
import ActualPrStats from '../ActualPrStats/ActualPrStats';
import { ManageBotActionsProps } from './ManageBotActions.types';

const ManageBotActions: FC<ManageBotActionsProps> = ({
  isVisible,
  bot,
  tableId,
  room,
  onHide,
}) => {
  const pid = bot.pid;
  const [isBotTracked, setIsBotTracked] = useState(false);
  const [balance, setBalance] = useState<number | undefined>();

  const hasManageBotsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS),
    [authStore.authenticatedUser],
  );

  const hasManageBotsParamsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS_PARAMS),
    [authStore.authenticatedUser],
  );

  const isSuncity = useMemo(() => getPokerRoom() === PokerRoom.Suncity, []);

  const isClubGG = useMemo(() => getPokerRoom() === PokerRoom.ClubGG, []);

  const isPokerrrr2 = useMemo(() => getPokerRoom() === PokerRoom.Pokerrrr2, []);

  const isXpoker = useMemo(() => getPokerRoom() === PokerRoom.Xpoker, []);

  const isAKGame = useMemo(() => getPokerRoom() === PokerRoom.AKGame, []);

  const isPokerBros = useMemo(() => getPokerRoom() === PokerRoom.PokerBros, []);

  const client = useStompClient();

  const rebuyRef = useRef(0);

  useEffect(() => {
    if (isVisible) {
      setIsBotTracked(tableStore.isBotTracked(pid));
      availableBotStore
        .getAvailableBot(bot.pid)
        .then(availableBot => setBalance(availableBot?.balance))
        .catch(() => null);
    }
  }, [isVisible]);

  const prStats = useMemo(() => {
    if (isVisible && bot.prStats && hasManageBotsParamsPermission) {
      const entries = bot.prStats.split(';').map(entry => {
        const [name, value] = entry.split(':');
        return { name, value };
      });
      return (
        <div className="prstats" style={{ width: isSuncity ? 600 : 480 }}>
          <b>RSI PRSTATS</b>
          <div className="prstats-info">
            {entries.map(({ name, value }) => (
              <div key={name} className="prstats-entry">
                {`${name}: ${value}`}
              </div>
            ))}
          </div>
        </div>
      );
    }
  }, [isVisible, bot.prStats, isSuncity, hasManageBotsParamsPermission]);

  const onActionClick = (action: () => void): void => {
    action();
    onHide();
  };

  const onRemoveBot = (onUTG: boolean): void => {
    confirmDialog({
      message: (
        <>
          Are you sure you want to remove the bot (<b>{pid}</b>)<br /> from the
          table
          {onUTG ? <b>{' on UTG'}</b> : ''}?
        </>
      ),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        botManagementStore.removeBotFromTable(client, tableId, room, pid, {
          onUTG,
        });
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent for the bot (<b>{pid}</b>) to leave the table
              {onUTG ? <b>{' on UTG'}</b> : ''}
            </>
          ),
        });
      },
    });
  };

  const onCancelRemoveOnUTG = (): void => {
    confirmDialog({
      message: (
        <>
          Are you sure you want to cancel leaving the bot (<b>{pid}</b>)<br />
          from the table on UTG?
        </>
      ),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        botManagementStore.cancelRemoveBotFromTableOnUTG(
          client,
          tableId,
          room,
          pid,
        );
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent for the bot (<b>{pid}</b>) to cancel leaving the
              table on UTG
            </>
          ),
        });
      },
    });
  };

  const onSitOutBot = (sitOut: boolean): void => {
    confirmDialog({
      message: (
        <>
          Are you sure you want to
          {sitOut ? <b>{' back '}</b> : <b>{' Sit Out '}</b>}
          the bot (<b>{pid}</b>)<br /> {sitOut ? 'to ' : 'from '}
          the table?
        </>
      ),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        botManagementStore.sitOutBotFromTable(client, tableId, room, pid, {
          sitOut: !sitOut,
        });
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent for the bot (<b>{pid}</b>) to
              {sitOut ? <b>{' back to '}</b> : <b>{' Sit Out from '}</b>}
              the table
            </>
          ),
        });
      },
    });
  };

  const onBotChangeTable = (): void => {
    confirmDialog({
      message: (
        <>
          Are you sure you want to change the table <br /> for the bot (
          <b>{pid}</b>
          )?
        </>
      ),
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        botManagementStore.changeTable(client, tableId, room, pid);
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent for the bot (<b>{pid}</b>) to change the table
            </>
          ),
        });
      },
    });
  };

  const onRebuy = (): void => {
    confirmDialog({
      message: (
        <>
          <label htmlFor="rebuy-input" className="block mb-2">
            Enter rebuy:
          </label>
          <InputNumber
            id="rebuy-input"
            value={0}
            onChange={e => (rebuyRef.current = e.value)}
            inputClassName="buyin-input"
          />
          BB
        </>
      ),
      className: 'rebuy-dialog w-auto',
      header: 'Rebuy',
      icon: 'pi pi-wallet',
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      accept: () => {
        botManagementStore.rebuy(client, tableId, room, pid, {
          rebuy: rebuyRef.current,
        });
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent for the bot (<b>{pid}</b>) to rebuy
            </>
          ),
        });
      },
    });
  };

  const onTrackBot = (): void => {
    tableStore.trackBot(pid);
  };

  const onStopTrackingBot = (): void => {
    tableStore.stopTrackingBot(pid);
  };

  const actions = (
    <div className="action-btns">
      <Button
        label={isBotTracked ? 'Stop tracking' : 'Track bot'}
        severity="help"
        onClick={() =>
          onActionClick(isBotTracked ? onStopTrackingBot : onTrackBot)
        }
      />
      {hasManageBotsPermission && (
        <>
          <Button
            label="Leave table"
            severity="danger"
            onClick={() => onActionClick(() => onRemoveBot(false))}
          />
          <div className="pair-btns">
            <Button
              label="Leave table on UTG"
              className="w-100"
              severity="warning"
              onClick={() => onActionClick(() => onRemoveBot(true))}
              disabled={bot.leaveOnUTG}
            />
            <Button
              label="Cancel leave on UTG"
              severity="warning"
              onClick={() => onActionClick(onCancelRemoveOnUTG)}
              disabled={!bot.leaveOnUTG}
            />
          </div>
          {(isClubGG || isPokerrrr2 || isXpoker || isPokerBros) && (
            <div className="pair-btns">
              <Button
                label="Sit Out"
                className="w-100"
                severity="secondary"
                onClick={() => onActionClick(() => onSitOutBot(bot.sittingOut))}
                disabled={bot.sittingOut}
              />
              <Button
                label="I'm back"
                severity="secondary"
                onClick={() => onActionClick(() => onSitOutBot(bot.sittingOut))}
                disabled={!bot.sittingOut}
              />
            </div>
          )}
          {(isSuncity || isAKGame) && (
            <Button
              label="Change table"
              onClick={() => onActionClick(onBotChangeTable)}
            />
          )}
          {userSettingsStore.settings.table.rebuy && (
            <Button
              label="Rebuy"
              severity="info"
              onClick={() => onActionClick(onRebuy)}
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <Dialog
      visible={isVisible}
      onHide={onHide}
      header="Bot menu"
      headerClassName="text-center"
      className="ManageBotActions-Component"
      footer={actions}
      pt={{
        root: { onClick: (e: React.MouseEvent) => e.stopPropagation() },
        mask: { onClick: (e: React.MouseEvent) => e.stopPropagation() },
        closeButton: { onClick: (e: React.MouseEvent) => e.stopPropagation() },
      }}
    >
      {balance !== undefined && (
        <div className="balance">
          <b>BALANCE:</b> {balance}
        </div>
      )}
      <ActualPrStats actualPrStats={bot.actualPrStats} />
      {prStats}
    </Dialog>
  );
};

export default ManageBotActions;
